import React, { useState, useEffect } from 'react'
import {
  Shield,
  Lock,
  Eye,
  Users,
  FileCheck,
  Globe,
  ChevronRight,
  CheckCircle,
  Server,
  Database,
  Network,
  Zap,
  ArrowUpRight,
  Download,
  ExternalLink,
  Calendar,
  MapPin,
  Building,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  Brain,
  ChevronDown,
  ChevronUp,
  Menu,
  X
} from 'lucide-react'
import RequestAccessModal from '../components/RequestAccessModal'
import PasskeyOnboarding from '../components/PasskeyOnboarding'
import PrivacyPortalSection from '../components/PrivacyPortalSection'
import RankBadges from '../components/RankBadges'
import { useTenant } from '../contexts/TenantContext'
import { tenantFetch } from '../lib/tenant-fetch'
import { CAIQ_V4 } from '../data/caiq-v4-template'

const TrustCenterEnhanced = () => {
  const { currentTenant, loading: tenantLoading } = useTenant()
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [activeSection, setActiveSection] = useState('overview')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Dynamic content state
  const [complianceBadges, setComplianceBadges] = useState([])
  const [securityFeatures, setSecurityFeatures] = useState([])
  const [subprocessors, setSubprocessors] = useState([])
  const [documents, setDocuments] = useState([])
  const [heroContent, setHeroContent] = useState(null)
  const [systemSettings, setSystemSettings] = useState(null)
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState<string>('all')
  const [subprocessorProductFilter, setSubprocessorProductFilter] = useState<string>('all')
  const [knowledgeBase, setKnowledgeBase] = useState<any>(null)
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())

  // Update favicon when systemSettings loads
  useEffect(() => {
    if (systemSettings?.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link')
      link.type = 'image/x-icon'
      link.rel = 'shortcut icon'
      link.href = systemSettings.faviconUrl
      document.getElementsByTagName('head')[0].appendChild(link)
    }
  }, [systemSettings])
  const [loading, setLoading] = useState(true)

  // Fetch content on component mount
  useEffect(() => {
    // Don't fetch until tenant is loaded
    if (tenantLoading || !currentTenant) {
      return
    }

    const fetchContent = async () => {
      try {
        setLoading(true)

        // Fetch all content in parallel with tenant context
        const [badgesRes, featuresRes, subprocessorsRes, heroRes, settingsRes, productsRes, kbRes, documentsRes] = await Promise.allSettled([
          tenantFetch('/api/badges', { tenantId: currentTenant.id }),
          tenantFetch('/api/security-features', { tenantId: currentTenant.id }),
          tenantFetch('/api/subprocessors', { tenantId: currentTenant.id }),
          tenantFetch('/api/hero', { tenantId: currentTenant.id }),
          tenantFetch('/api/settings-db', { tenantId: currentTenant.id }),
          tenantFetch('/api/products', { tenantId: currentTenant.id }),
          tenantFetch(`/api/get-kb?tenantId=${currentTenant.id}`),
          tenantFetch('/api/documents', { tenantId: currentTenant.id })
        ])

        // Log rejected promises
        if (badgesRes.status === 'rejected') {
          console.error('Failed to fetch badges:', badgesRes.reason)
        }
        if (featuresRes.status === 'rejected') {
          console.error('Failed to fetch features:', featuresRes.reason)
        }
        if (subprocessorsRes.status === 'rejected') {
          console.error('Failed to fetch subprocessors:', subprocessorsRes.reason)
        }
        if (heroRes.status === 'rejected') {
          console.error('Failed to fetch hero:', heroRes.reason)
        }
        if (settingsRes.status === 'rejected') {
          console.error('Failed to fetch settings:', settingsRes.reason)
        }
        if (productsRes.status === 'rejected') {
          console.error('Failed to fetch products:', productsRes.reason)
        }
        if (kbRes.status === 'rejected') {
          console.error('Failed to fetch knowledge base:', kbRes.reason)
        }
        if (documentsRes.status === 'rejected') {
          console.error('Failed to fetch documents:', documentsRes.reason)
        }

        // Handle badges
        if (badgesRes.status === 'fulfilled') {
          if (badgesRes.value.ok) {
            const badgesData = await badgesRes.value.json()
            // Extract array from various response formats
            const badges = Array.isArray(badgesData) ? badgesData :
                          (badgesData?.result?.badges || badgesData?.badges || [])
            const badgesWithIcons = badges.map((badge: any) => ({
              ...badge,
              icon: getIconComponent(badge.icon)
            }))
            setComplianceBadges(badgesWithIcons)
          } else {
            console.error('API failed:', '/api/badges', badgesRes.value.status)
            try {
              const errorBody = await badgesRes.value.text()
              console.error('Error body:', errorBody)
            } catch (e) {
              console.error('Could not read error body')
            }
          }
        }

        // Handle security features
        if (featuresRes.status === 'fulfilled') {
          if (featuresRes.value.ok) {
            const featuresData = await featuresRes.value.json()
            // Extract array from various response formats
            const features = Array.isArray(featuresData) ? featuresData :
                            (featuresData?.result?.features || featuresData?.features || [])
            const featuresWithIcons = features.map((feature: any) => ({
              ...feature,
              icon: getIconComponent(feature.icon)
            }))
            setSecurityFeatures(featuresWithIcons)
          } else {
            console.error('API failed:', '/api/security-features', featuresRes.value.status)
          }
        }

        // Handle subprocessors
        if (subprocessorsRes.status === 'fulfilled') {
          if (subprocessorsRes.value.ok) {
            try {
              const subprocessorsData = await subprocessorsRes.value.json()
              // Extract array from various response formats
              const vendors = Array.isArray(subprocessorsData) ? subprocessorsData :
                             (subprocessorsData?.result?.subprocessors ||
                              subprocessorsData?.subprocessors ||
                              subprocessorsData?.result?.vendors || [])
              setSubprocessors(vendors)
            } catch {
              setSubprocessors([])
            }
          } else {
            console.error('API failed:', '/api/subprocessors', subprocessorsRes.value.status)
          }
        }

        // Handle hero content
        if (heroRes.status === 'fulfilled') {
          if (heroRes.value.ok) {
            const heroData = await heroRes.value.json()
            const hero = heroData.success ? heroData.result : heroData
            setHeroContent(hero?.general || hero)
          } else {
            console.error('API failed:', '/api/hero', heroRes.value.status)
          }
        }

        // Handle system settings
        if (settingsRes.status === 'fulfilled') {
          if (settingsRes.value.ok) {
            const settingsData = await settingsRes.value.json()
            const settings = settingsData.success ? settingsData.result : settingsData
            setSystemSettings(settings)
          } else {
            console.error('API failed:', '/api/settings-db', settingsRes.value.status)
          }
        }

        // Handle products
        if (productsRes.status === 'fulfilled') {
          if (productsRes.value.ok) {
            const productsData = await productsRes.value.json()
            // Extract array from various response formats
            const products = Array.isArray(productsData) ? productsData :
                            (productsData?.result?.products || productsData?.products || [])
            setProducts(products)
          } else {
            console.error('API failed:', '/api/products', productsRes.value.status)
            try {
              const errorBody = await productsRes.value.text()
              console.error('Error body:', errorBody)
            } catch (e) {
              console.error('Could not read error body')
            }
          }
        }

        // Handle knowledge base
        if (kbRes.status === 'fulfilled') {
          if (kbRes.value.ok) {
            const kbData = await kbRes.value.json()
            if (kbData.found) {
              setKnowledgeBase(kbData)
            }
          } else {
            console.error('API failed:', `/api/get-kb?tenantId=${currentTenant.id}`, kbRes.value.status)
            try {
              const errorBody = await kbRes.value.text()
              console.error('Error body:', errorBody)
            } catch (e) {
              console.error('Could not read error body')
            }
          }
        }

        // Handle documents
        if (documentsRes.status === 'fulfilled') {
          if (documentsRes.value.ok) {
            const documentsData = await documentsRes.value.json()
            // Extract array from various response formats
            const docs = Array.isArray(documentsData) ? documentsData :
                        (documentsData?.result?.documents || documentsData?.documents || [])
            // Filter to only show active, non-archived, non-deleted documents
            const visibleDocs = docs.filter((doc: any) =>
              !doc.archived && !doc.deleted
            )
            setDocuments(visibleDocs)
          } else {
            console.error('API failed:', '/api/documents', documentsRes.value.status)
            try {
              const errorBody = await documentsRes.value.text()
              console.error('Error body:', errorBody)
            } catch (e) {
              console.error('Could not read error body')
            }
          }
        }

      } catch (error) {
        console.error('Error fetching content:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [currentTenant, tenantLoading])

  // Helper function to map icon names to components
  const getIconComponent = (iconName: string) => {
    const iconMap: any = {
      Shield, Lock, Eye, Users, FileCheck, Globe, Server, Database, Network,
      Zap, CheckCircle, ArrowUpRight, Download, ExternalLink, Calendar,
      MapPin, Building, Mail, Phone, Clock, AlertTriangle, CheckCircle2
    }
    return iconMap[iconName] || Shield
  }

  const architectureHighlights = [
    {
      title: 'Multi-Region Deployment',
      description: 'Global infrastructure with data residency compliance',
      icon: Globe,
      regions: ['US East', 'US West', 'EU West', 'Asia Pacific']
    },
    {
      title: 'Edge Security',
      description: 'Distributed security enforcement at the network edge',
      icon: Network,
      features: ['WAF', 'Bot Protection', 'Rate Limiting', 'Geo-blocking']
    },
    {
      title: 'Monitoring & Alerting',
      description: 'Real-time security monitoring and incident response',
      icon: Eye,
      capabilities: ['SIEM Integration', 'Threat Detection', 'Automated Response', 'Forensic Analysis']
    }
  ]


  const systemStatus = {
    overall: 'operational',
    lastIncident: '2024-01-10',
    uptime: '99.98%',
    components: [
      { name: 'API Services', status: 'operational' },
      { name: 'Web Application', status: 'operational' },
      { name: 'Database', status: 'operational' },
      { name: 'File Storage', status: 'operational' },
      { name: 'Email Delivery', status: 'degraded' }
    ]
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'text-green-600 bg-green-100'
      case 'degraded': return 'text-yellow-600 bg-yellow-100'
      case 'outage': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational': return <CheckCircle2 className="h-4 w-4" />
      case 'degraded': return <AlertTriangle className="h-4 w-4" />
      case 'outage': return <AlertTriangle className="h-4 w-4" />
      default: return <Clock className="h-4 w-4" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-netlify-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Trust Center...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-netlify-light">
      {/* Dynamic Theme Styles */}
      {systemSettings && (
        <style>
          {`
            :root {
              --primary-50: ${systemSettings.primaryColor ? `${systemSettings.primaryColor}10` : '#f0fdf9'};
              --primary-100: ${systemSettings.primaryColor ? `${systemSettings.primaryColor}20` : '#ccfbf1'};
              --primary-500: ${systemSettings.primaryColor || '#2E51ED'};
              --primary-600: ${systemSettings.primaryColor || '#2E51ED'};
              --primary-700: ${systemSettings.primaryColor ? `${systemSettings.primaryColor}E6` : '#1E40AF'};
              --primary-800: ${systemSettings.primaryColor ? `${systemSettings.primaryColor}CC` : '#1E3A8A'};
              --netlify-500: ${systemSettings.primaryColor || '#2E51ED'};
              --netlify-600: ${systemSettings.primaryColor || '#2E51ED'};
              --netlify-800: ${systemSettings.primaryColor ? `${systemSettings.primaryColor}CC` : '#1E3A8A'};
              --secondary-color: ${systemSettings.secondaryColor || '#05BDBA'};
            }
            .bg-primary-600 { background-color: var(--primary-600) !important; }
            .bg-primary-700 { background-color: var(--primary-700) !important; }
            .text-primary-600 { color: var(--primary-600) !important; }
            .text-primary-700 { color: var(--primary-700) !important; }
            .border-primary-500 { border-color: var(--primary-500) !important; }
            .border-primary-300 { border-color: var(--primary-500)60 !important; }
            .hover\\:border-primary-300:hover { border-color: var(--primary-500)60 !important; }
            .focus\\:ring-primary-500:focus { --tw-ring-color: var(--primary-500) !important; }
            .bg-netlify-500 { background-color: var(--netlify-500) !important; }
            .bg-netlify-600 { background-color: var(--netlify-600) !important; }
            .text-netlify-600 { color: var(--netlify-600) !important; }
            .text-netlify-800 { color: var(--netlify-800) !important; }
            .hover\\:bg-primary-700:hover { background-color: var(--primary-700) !important; }
            .hover\\:text-primary-700:hover { color: var(--primary-700) !important; }
            .from-netlify-500 { --tw-gradient-from: var(--netlify-500) !important; }
            .to-netlify-600 { --tw-gradient-to: var(--netlify-600) !important; }
          `}
        </style>
      )}
      {/* Header */}
      <header className="bg-white border-b border-netlify-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                {systemSettings?.logoUrl ? (
                  <img src={systemSettings.logoUrl} alt="Logo" className="w-8 h-8 rounded" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-netlify-500 to-netlify-600 rounded flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {systemSettings?.brandName || systemSettings?.companyName || currentTenant?.name || 'Trust Center'}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 -mt-1">Trust Center</p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => setActiveSection('overview')}
                className={`text-sm font-medium ${
                  activeSection === 'overview' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              {securityFeatures.length > 0 && (
                <button
                  onClick={() => setActiveSection('security')}
                  className={`text-sm font-medium ${
                    activeSection === 'security' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Security
                </button>
              )}
              {complianceBadges.length > 0 && (
                <button
                  onClick={() => setActiveSection('compliance')}
                  className={`text-sm font-medium ${
                    activeSection === 'compliance' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Compliance
                </button>
              )}
              {systemSettings?.privacyPolicyUrl && (
                <button
                  onClick={() => setActiveSection('privacy')}
                  className={`text-sm font-medium ${
                    activeSection === 'privacy' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Privacy
                </button>
              )}
              {subprocessors.length > 0 && (
                <a
                  href={systemSettings?.subprocessorsPageUrl || '/subprocessors'}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Subprocessors
                </a>
              )}
              {knowledgeBase && Object.keys(knowledgeBase.answers || {}).length > 0 && (
                <button
                  onClick={() => setActiveSection('knowledge-base')}
                  className={`text-sm font-medium ${
                    activeSection === 'knowledge-base' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Knowledge Base
                </button>
              )}
              {documents.length > 0 && (
                <button
                  onClick={() => setActiveSection('library')}
                  className={`text-sm font-medium ${
                    activeSection === 'library' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Library
                </button>
              )}
            </nav>

            {/* Desktop CTA Button */}
            <div className="hidden md:block">
              {localStorage.getItem('auth-token') ? (
                <button
                  onClick={() => window.location.href = '/profile'}
                  className="btn-primary inline-flex items-center"
                >
                  My Profile
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = '/login'}
                  className="btn-primary inline-flex items-center"
                >
                  Sign In
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 pt-2 border-t border-gray-200">
              <nav className="flex flex-col space-y-3">
                <button
                  onClick={() => {
                    setActiveSection('overview')
                    setMobileMenuOpen(false)
                  }}
                  className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                    activeSection === 'overview'
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Overview
                </button>
                {securityFeatures.length > 0 && (
                  <button
                    onClick={() => {
                      setActiveSection('security')
                      setMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                      activeSection === 'security'
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Security
                  </button>
                )}
                {complianceBadges.length > 0 && (
                  <button
                    onClick={() => {
                      setActiveSection('compliance')
                      setMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                      activeSection === 'compliance'
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Compliance
                  </button>
                )}
                {systemSettings?.privacyPolicyUrl && (
                  <button
                    onClick={() => {
                      setActiveSection('privacy')
                      setMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                      activeSection === 'privacy'
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Privacy
                  </button>
                )}
                {subprocessors.length > 0 && (
                  <a
                    href={systemSettings?.subprocessorsPageUrl || '/subprocessors'}
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Subprocessors
                  </a>
                )}
                {knowledgeBase && Object.keys(knowledgeBase.answers || {}).length > 0 && (
                  <button
                    onClick={() => {
                      setActiveSection('knowledge-base')
                      setMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                      activeSection === 'knowledge-base'
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Knowledge Base
                  </button>
                )}
                {documents.length > 0 && (
                  <button
                    onClick={() => {
                      setActiveSection('library')
                      setMobileMenuOpen(false)
                    }}
                    className={`text-left px-4 py-2 rounded-md text-base font-medium ${
                      activeSection === 'library'
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Library
                  </button>
                )}
                <div className="pt-2 border-t border-gray-200">
                  {localStorage.getItem('auth-token') ? (
                    <button
                      onClick={() => window.location.href = '/profile'}
                      className="w-full btn-primary inline-flex items-center justify-center"
                    >
                      My Profile
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => window.location.href = '/login'}
                      className="w-full btn-primary inline-flex items-center justify-center"
                    >
                      Sign In
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-netlify-50 via-netlify-100 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-netlify-900 sm:text-5xl md:text-6xl">
              {heroContent?.title || `${currentTenant?.name || 'Security'}`}
              <span className="block text-netlify-600">{heroContent?.subtitle || 'Trust Center'}</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-netlify-700 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              {heroContent?.description || `Your comprehensive resource for ${currentTenant?.name || 'our'} security practices, compliance certifications, and transparency commitments.`}
            </p>

            {/* Quick Stats - Only show if configured */}
            {heroContent?.stats?.length > 0 && (
              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {heroContent.stats.map((stat: any, index: number) => (
                  <div key={index} className="text-center bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-netlify-200">
                    <div className="text-3xl font-bold text-netlify-600">{stat.value}</div>
                    <div className="text-sm text-netlify-700">{stat.label}</div>
                    {stat.description && (
                      <div className="text-xs text-gray-600 mt-1">{stat.description}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8">
              <button
                onClick={() => window.location.href = '/login'}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Sign In to Access Evidence Library
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <>
          {/* System Status - Only show if status page URL is configured */}
          {systemSettings?.statusPageUrl && (
            <section className="py-12 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">System Status</h2>
                    <div className="flex items-center">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(systemStatus.overall)}`}>
                        {getStatusIcon(systemStatus.overall)}
                        <span className="ml-2 capitalize">{systemStatus.overall}</span>
                      </div>
                      <a href={systemSettings.statusPageUrl} className="ml-4 text-sm text-primary-600 hover:text-primary-700 inline-flex items-center">
                        View Status Page
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {systemStatus.components.map((component, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-900">{component.name}</span>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusColor(component.status)}`}>
                          {getStatusIcon(component.status)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 text-sm text-gray-600">
                    <span>Last incident: {new Date(systemStatus.lastIncident).toLocaleDateString()}</span>
                    <span className="mx-2">•</span>
                    <span>Uptime: {systemStatus.uptime}</span>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Security Section */}
      {activeSection === 'security' && (
        <>
          {/* Security Architecture */}
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Security Architecture</h2>
                <p className="text-lg text-gray-600">Defense-in-depth security model protecting your data</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                {architectureHighlights.map((item, index) => {
                  const IconComponent = item.icon
                  return (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                      <IconComponent className="h-8 w-8 text-primary-600 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 mb-4">{item.description}</p>
                      {'regions' in item && (
                        <div className="space-y-1">
                          {item.regions.map((region, idx) => (
                            <span key={idx} className="inline-block bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded mr-2">
                              {region}
                            </span>
                          ))}
                        </div>
                      )}
                      {'features' in item && (
                        <div className="space-y-1">
                          {item.features.map((feature, idx) => (
                            <span key={idx} className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded mr-2">
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}
                      {'capabilities' in item && (
                        <div className="space-y-1">
                          {item.capabilities.map((capability, idx) => (
                            <span key={idx} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded mr-2">
                              {capability}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Product Filter */}
              {products.length > 0 && (
                <div className="mb-8 flex justify-center">
                  <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
                    <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                    <select
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="all">All Products</option>
                      {products.map((product: any) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Security Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {securityFeatures
                  .filter((feature: any) => {
                    if (selectedProduct === 'all') return true
                    if (!feature.productIds || feature.productIds.length === 0) return true
                    return feature.productIds.includes(selectedProduct)
                  })
                  .map((feature, index) => {
                  const IconComponent = feature.icon
                  return (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                      <div className="flex items-start">
                        <IconComponent className="h-6 w-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                          <p className="text-gray-600 mb-4">{feature.description}</p>
                          <ul className="space-y-1">
                            {feature.details.map((detail, idx) => (
                              <li key={idx} className="flex items-center text-sm text-gray-600">
                                <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Compliance Section */}
      {activeSection === 'compliance' && (
        <>
          {/* Compliance Badges */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Security Certifications</h2>
                <p className="text-lg text-gray-600 mb-4">Industry-recognized security and compliance standards</p>
                {currentTenant?.id && (
                  <div className="flex justify-center mb-8">
                    <RankBadges tenantId={currentTenant.id} />
                  </div>
                )}
              </div>

              {/* Product Filter */}
              {products.length > 0 && (
                <div className="mb-12 flex justify-center">
                  <div className="inline-flex items-center gap-3 bg-gray-50 px-6 py-3 rounded-lg border border-gray-200">
                    <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                    <select
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
                    >
                      <option value="all">All Products</option>
                      {products.map((product: any) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {complianceBadges
                  .filter((badge: any) => {
                    if (selectedProduct === 'all') return true
                    if (!badge.productIds || badge.productIds.length === 0) return true
                    return badge.productIds.includes(selectedProduct)
                  })
                  .map((badge, index) => {
                  const IconComponent = badge.icon
                  return (
                    <div key={index} className="bg-white rounded-lg p-6 border-2 border-gray-200 hover:border-primary-300 transition-colors group">
                      <div className="flex items-start justify-between mb-4">
                        <IconComponent className="h-12 w-12 text-primary-600" />
                        {badge.reportAvailable && (
                          <button
                            onClick={() => setShowRequestModal(true)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-primary-600 hover:text-primary-700 inline-flex items-center"
                          >
                            View Report
                            <ArrowUpRight className="h-3 w-3 ml-1" />
                          </button>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{badge.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{badge.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-600">{badge.status}</span>
                        <span className="text-xs text-gray-500">{badge.year}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Subprocessors Section */}
      {activeSection === 'subprocessors' && (
        <>
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Subprocessors</h2>
                <p className="text-lg text-gray-600 mb-8">Third-party vendors we work with to deliver our services</p>
              </div>

              {/* Product Filter - Only show if products exist */}
              {products.length > 0 && (
                <div className="mb-6 flex justify-center">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                    <select
                      value={subprocessorProductFilter}
                      onChange={(e) => setSubprocessorProductFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="all">All Products</option>
                      {products.map((product: any) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Current Subprocessors</h3>
                    <div className="flex space-x-4 text-sm text-gray-600">
                      {systemSettings?.lastUpdatedText && (
                        <span>Last updated: {systemSettings.lastUpdatedText}</span>
                      )}
                      {systemSettings?.subprocessorsPageUrl && (
                        <a href={systemSettings.subprocessorsPageUrl} className="text-primary-600 hover:text-primary-700 inline-flex items-center">
                          {systemSettings?.viewFullListText || 'View Full List'}
                          <ArrowUpRight className="h-3 w-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {subprocessors
                    .filter((processor: any) => {
                      // Exclude archived and deleted subprocessors
                      if (processor.archived || processor.deleted) return false

                      // Filter by product
                      if (subprocessorProductFilter === 'all') return true
                      if (!processor.productIds || processor.productIds.length === 0) return true
                      return processor.productIds.includes(subprocessorProductFilter)
                    })
                    .map((processor, index) => (
                    <div key={index} className="px-6 py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h4 className="font-semibold text-gray-900">{processor.name}</h4>
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {processor.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{processor.purpose}</p>
                          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                            <span className="inline-flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {processor.region}
                            </span>
                            <span className="inline-flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              Last reviewed: {new Date(processor.lastReviewed).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <a
                            href={processor.dpaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center"
                          >
                            DPA
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Privacy Section */}
      {activeSection === 'privacy' && (
        <PrivacyPortalSection />
      )}

      {/* Knowledge Base Section */}
      {activeSection === 'knowledge-base' && (
        <>
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <Brain className="h-10 w-10 text-primary-600 mr-3" />
                  <h2 className="text-3xl font-bold text-gray-900">Security Knowledge Base</h2>
                </div>
                <p className="text-lg text-gray-600 mb-4">
                  Comprehensive answers to security and compliance questions based on CAIQ v4
                </p>
                {knowledgeBase && (
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                    <span className="inline-flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                      {Object.keys(knowledgeBase.answers || {}).length} questions answered
                    </span>
                    <span className="text-gray-400">•</span>
                    <span>{knowledgeBase.completion?.toFixed(0) || 0}% complete</span>
                    {knowledgeBase.lastUpdated && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span>Last updated: {new Date(knowledgeBase.lastUpdated).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {knowledgeBase && Object.keys(knowledgeBase.answers || {}).length > 0 ? (
                <div className="space-y-8">
                  {CAIQ_V4.map((domain) => {
                    const domainAnswers = domain.questions.filter(q => knowledgeBase.answers[q.id])

                    if (domainAnswers.length === 0) return null

                    return (
                      <div key={domain.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900">{domain.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{domain.description}</p>
                          <div className="mt-2 text-sm text-gray-500">
                            {domainAnswers.length} of {domain.questions.length} questions answered
                          </div>
                        </div>

                        <div className="divide-y divide-gray-200">
                          {domainAnswers.map((question) => {
                            const answer = knowledgeBase.answers[question.id]
                            const isExpanded = expandedQuestions.has(question.id)

                            return (
                              <div key={question.id} className="px-6 py-4">
                                <button
                                  onClick={() => {
                                    const newSet = new Set(expandedQuestions)
                                    if (isExpanded) {
                                      newSet.delete(question.id)
                                    } else {
                                      newSet.add(question.id)
                                    }
                                    setExpandedQuestions(newSet)
                                  }}
                                  className="w-full text-left flex items-start justify-between gap-4 hover:bg-gray-50 -mx-6 px-6 py-2 rounded-lg transition"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-start gap-3">
                                      <span className="text-xs font-mono text-gray-500 mt-0.5 flex-shrink-0">
                                        {question.id}
                                      </span>
                                      <div className="flex-1">
                                        <h4 className="font-medium text-gray-900">{question.question}</h4>
                                        {answer && (
                                          <div className="mt-2 flex items-center gap-2">
                                            {answer.answer === 'yes' && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Yes
                                              </span>
                                            )}
                                            {answer.answer === 'no' && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                No
                                              </span>
                                            )}
                                            {answer.confidence && (
                                              <span className="text-xs text-gray-500">
                                                Confidence: {Math.round(answer.confidence * 100)}%
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                  )}
                                </button>

                                {isExpanded && answer && (
                                  <div className="mt-4 pl-20 pr-10">
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Explanation</h5>
                                      <p className="text-sm text-gray-700 leading-relaxed">
                                        {answer.explanation}
                                      </p>

                                      {answer.sources && answer.sources.length > 0 && (
                                        <div className="mt-4">
                                          <h5 className="text-sm font-semibold text-gray-900 mb-2">Sources</h5>
                                          <div className="space-y-2">
                                            {answer.sources.map((source: any, idx: number) => (
                                              <div key={idx} className="text-xs text-gray-600 bg-white rounded p-2 border border-gray-200">
                                                <div className="font-medium text-gray-900">{source.source}</div>
                                                {source.excerpt && (
                                                  <div className="mt-1 text-gray-600 italic">
                                                    "{source.excerpt}"
                                                  </div>
                                                )}
                                                {source.date && (
                                                  <div className="mt-1 text-gray-500">
                                                    {new Date(source.date).toLocaleDateString()}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {answer.approvedBy && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                          <div className="text-xs text-gray-500">
                                            Approved by {answer.approvedBy}
                                            {answer.approvedAt && ` on ${new Date(answer.approvedAt).toLocaleDateString()}`}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Knowledge Base Coming Soon
                  </h3>
                  <p className="text-gray-600">
                    We're currently building our comprehensive security knowledge base.
                    Check back soon for detailed answers to common security and compliance questions.
                  </p>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Library Section */}
      {activeSection === 'library' && (
        <>
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <BookOpen className="h-10 w-10 text-primary-600 mr-3" />
                  <h2 className="text-3xl font-bold text-gray-900">Document Library</h2>
                </div>
                <p className="text-lg text-gray-600">
                  Browse our available security and compliance documentation
                </p>
              </div>

              {documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {documents.map((doc: any) => (
                    <div key={doc.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <FileCheck className="h-5 w-5 text-primary-600 mr-2" />
                            <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                          </div>
                        </div>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mb-4">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {doc.type && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {doc.type}
                            </span>
                          )}
                          {doc.category && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {doc.category}
                            </span>
                          )}
                        </div>
                        <a
                          href={`/documents/${doc.id}`}
                          className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                        >
                          View Document
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileCheck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Documents Available
                  </h3>
                  <p className="text-gray-600">
                    Check back soon for security and compliance documentation.
                  </p>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* CTA Section */}
      <section className="bg-primary-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need Access to Detailed Evidence?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Get access to our complete security documentation, audit reports,
            and compliance certificates under NDA.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Sign In or Request Access
            <ChevronRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-primary-400" />
                <span className="ml-2 text-lg font-semibold">Trust Center</span>
              </div>
              <p className="text-gray-400 text-sm">
                Your security is our priority. Access comprehensive documentation
                and evidence of our security practices.
              </p>
            </div>
            {(systemSettings?.privacyPolicyUrl || systemSettings?.termsOfServiceUrl || systemSettings?.dpaUrl) && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Security</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  {systemSettings?.privacyPolicyUrl && (
                    <li><a href={systemSettings.privacyPolicyUrl} className="hover:text-white">Privacy Policy</a></li>
                  )}
                  {systemSettings?.termsOfServiceUrl && (
                    <li><a href={systemSettings.termsOfServiceUrl} className="hover:text-white">Terms of Service</a></li>
                  )}
                  {systemSettings?.dpaUrl && (
                    <li><a href={systemSettings.dpaUrl} className="hover:text-white">Data Processing Agreement</a></li>
                  )}
                </ul>
              </div>
            )}
            {(systemSettings?.soc2ReportUrl || systemSettings?.iso27001CertificateUrl || systemSettings?.subprocessorsPageUrl || systemSettings?.statusPageUrl) && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Compliance</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  {systemSettings?.soc2ReportUrl && (
                    <li><a href={systemSettings.soc2ReportUrl} className="hover:text-white">SOC 2 Report</a></li>
                  )}
                  {systemSettings?.iso27001CertificateUrl && (
                    <li><a href={systemSettings.iso27001CertificateUrl} className="hover:text-white">ISO 27001 Certificate</a></li>
                  )}
                  {systemSettings?.subprocessorsPageUrl && (
                    <li><a href={systemSettings.subprocessorsPageUrl} className="hover:text-white">Subprocessors</a></li>
                  )}
                  {systemSettings?.statusPageUrl && (
                    <li><a href={systemSettings.statusPageUrl} className="hover:text-white">System Status</a></li>
                  )}
                </ul>
              </div>
            )}
            {(systemSettings?.securityEmail || systemSettings?.supportEmail || systemSettings?.companyLocation) && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  {systemSettings?.securityEmail && (
                    <li className="flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {systemSettings.securityEmail}
                    </li>
                  )}
                  {systemSettings?.supportEmail && (
                    <li className="flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {systemSettings.supportEmail}
                    </li>
                  )}
                  {systemSettings?.companyLocation && (
                    <li className="flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      {systemSettings.companyLocation}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">&copy; 2024 {systemSettings?.companyName || currentTenant?.name || 'Company Name'}. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              {systemSettings?.termsOfServiceUrl && (
                <a href={systemSettings.termsOfServiceUrl} className="text-gray-400 hover:text-white text-sm">Terms</a>
              )}
              {systemSettings?.privacyPolicyUrl && (
                <a href={systemSettings.privacyPolicyUrl} className="text-gray-400 hover:text-white text-sm">Privacy</a>
              )}
              {systemSettings?.cookiePolicyUrl && (
                <a href={systemSettings.cookiePolicyUrl} className="text-gray-400 hover:text-white text-sm">Cookie Policy</a>
              )}
              <a href="/accessibility" className="text-gray-400 hover:text-white text-sm">Accessibility</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Request Access Modal */}
      <RequestAccessModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
      />

      {/* Passkey Onboarding Banner */}
      <PasskeyOnboarding />
    </div>
  )
}

export default TrustCenterEnhanced